import {
  validateEmail,
  validateUrl,
  validateUsPhoneNumber,
  validateZipCode,
  normalize,
  requiredError,
  deNullifyErrors,
  isNullish,
} from "@/utils/validations"
import validatePhotosMixin from "../../mixins/validatePhotosMixin"
const validatePhotos = validatePhotosMixin.methods.validatePhotos
function flattenErrors(errors) {
  const flat = {}

  const recurse = (obj) => {
    Object.values(obj).forEach((value) => {
      if (typeof value === "object" && value !== null) {
        recurse(value)
      } else if (value) {
        flat[Math.random()] = value
      }
    })
  }

  recurse(errors)
  return flat
}
export default {
  namespaced: true,

  state: () => ({
    fields: {
      profileUpload: null,
      showcaseUpload: [],
    },

    errors: {},
    currentStep: 0,

    formMessage: {
      show: false,
      type: null,
    },
  }),

  getters: {
    fields: (state) => state.fields,
    errors: (state) => state.errors,
    hasErrors: (state) => Object.keys(state.errors).length > 0,
    currentStep: (state) => state.currentStep,
    formMessage: (state) => state.formMessage,
  },

  mutations: {
    SET_FIELDS(state, payload) {
      state.fields = JSON.parse(JSON.stringify(payload))
    },

    UPDATE_FIELD(state, { key, value }) {
      state.fields[key] = value

      if (state.errors[key]) {
        delete state.errors[key]
      }
    },

    SET_ERRORS(state, errors) {
      state.errors = errors
    },

    CLEAR_ERRORS(state) {
      state.errors = {}
    },

    SET_STEP(state, step) {
      state.currentStep = step
    },

    SET_FORM_MESSAGE(state, payload) {
      state.formMessage = payload
    },

    CLEAR_FORM_MESSAGE(state) {
      state.formMessage = {
        show: false,
        type: null,
      }
    },
  },

  actions: {
    async validateStep(
      { dispatch, commit, state },
      { step, isMini, isEditMode }
    ) {
      let isValid = false

      switch (step) {
        case 0:
          isValid = await dispatch("validateContactInfo", {
            isMini,
            isEditMode,
          })
          break
        case 1:
          isValid = await dispatch("validateOverview")
          break
        case 2:
          isValid = await dispatch("validateBusinessServices")
          break
        case 3:
          isValid = await dispatch("validateAdditional")
          break
        case 4:
          isValid = await dispatch("validateEditPhotos")
          break
        default:
          isValid = true
      }

      if (!isValid) {
        const flatErrors = flattenErrors(state.errors)

        const hasMissingRequired = Object.values(flatErrors).some(
          (msg) =>
            msg === "This is required" ||
            msg === "Please enter your company name" ||
            msg === "Please enter your email address" ||
            msg === "Please enter your company ZIP code" ||
            msg === "Please enter a phone number"
        )

        commit("SET_FORM_MESSAGE", {
          show: true,
          type: hasMissingRequired ? "missing_fields" : "review_fields",
        })
      } else {
        commit("CLEAR_FORM_MESSAGE")
      }

      return isValid
    },

    validateContactInfo({ commit, state }, payload = {}) {
      const { isMini = false, isEditMode = false } = payload

      const f = state.fields

      const user =
        f.primaryUser && Object.keys(f.primaryUser).length
          ? f.primaryUser
          : f.principleContact || {}

      const principleContact = {
        firstName: user.firstName || "",
        lastName: user.lastName || "",
      }
      commit("UPDATE_FIELD", {
        key: "principleContact",
        value: principleContact,
      })

      const firstName = principleContact.firstName
      const lastName = principleContact.lastName
      const phoneNumber = f.phoneNumber || ""

      let errors = {}

      if (isMini) {
        errors = {
          firstName: !firstName ? "This is required" : null,
          lastName: !lastName ? "This is required" : null,
          name: !f.name ? "Please enter your company name" : null,
          zip: !f.zip
            ? "Please enter your company ZIP code"
            : !validateZipCode(f.zip)
            ? "Please enter a valid ZIP code"
            : null,
        }

        if (isEditMode) {
          errors.phoneNumber = !phoneNumber
            ? "Please enter a phone number"
            : !validateUsPhoneNumber(phoneNumber)
            ? "Please enter a valid phone number"
            : null
        }
      } else {
        errors = {
          firstName: !firstName ? "This is required" : null,
          lastName: !lastName ? "This is required" : null,
          email: !f.email
            ? "Please enter your email address"
            : !validateEmail(f.email)
            ? "Please enter a valid email address"
            : null,
          phoneNumber: isEditMode
            ? !phoneNumber
              ? "Please enter a phone number"
              : !validateUsPhoneNumber(phoneNumber)
              ? "Please enter a valid phone number"
              : null
            : phoneNumber && !validateUsPhoneNumber(phoneNumber)
            ? "Please enter a valid phone number"
            : null,
          website:
            f.website && !validateUrl(f.website)
              ? "Please enter a valid web address"
              : null,
          name: !f.name ? "Please enter your company name" : null,
          state: !f.state ? "This is required" : null,
          city: !f.city ? "This is required" : null,
          zip: !f.zip
            ? "Please enter your company ZIP code"
            : !validateZipCode(f.zip)
            ? "Please enter a valid ZIP code"
            : null,
        }
      }

      commit("SET_ERRORS", errors)
      const flatErrors = flattenErrors(errors)
      return Object.keys(flatErrors).length === 0
    },

    validateOverview({ commit }) {
      let errors = {}

      commit("SET_ERRORS", errors)
      return Object.keys(errors).length === 0
    },

    validateBusinessServices({ commit, state }) {
      const f = state.fields
      const isVendor = f?.provider?.isVendor
      const normalized = normalize(f)

      const errors = {
        provider: {
          isVendor:
            isVendor === undefined || isVendor === null
              ? "This is required"
              : null,
        },
        tradesRelatedToProduct: isVendor
          ? !normalized.tradesRelatedToProduct?.length
            ? "This is required"
            : null
          : null,
        providesInstallLaborServices: isVendor
          ? normalized.providesInstallLaborServices === undefined
            ? "This is required"
            : null
          : null,
      }

      commit("SET_ERRORS", errors)
      const flatErrors = flattenErrors(errors)
      return Object.keys(flatErrors).length === 0
    },

    validateAdditional({ commit, state }) {
      const f = state.fields

      const errors = {
        oshaCertification:
          f.oshaCertification === undefined || f.oshaCertification === null
            ? "This is required"
            : null,

        provider: {
          licenseType:
            f.provider?.licensed && !f.provider?.licenseType
              ? "This is required"
              : null,

          licenseNumber:
            f.provider?.licensed && !f.provider?.licenseNumber
              ? "This is required"
              : null,
        },
      }

      commit("SET_ERRORS", errors)
      const flatErrors = flattenErrors(errors)
      return Object.keys(flatErrors).length === 0
    },

    async validateEditPhotos({ commit, state }) {
      const f = state.fields

      const profileUpload = f.profileUpload ? [f.profileUpload] : []
      const showcaseUpload = f.showcaseUpload || []

      const errors = {
        profilePhotoUpload: profileUpload.length
          ? await validatePhotos(profileUpload)
          : null,

        showcasePhotosUpload: showcaseUpload.length
          ? await validatePhotos(showcaseUpload, undefined, false)
          : null,
      }

      commit("SET_ERRORS", errors)
      const flatErrors = flattenErrors(errors)
      return Object.keys(flatErrors).length === 0
    },

    validateCandidateBasicInfo({ commit, state }) {
      const f = state.fields

      let errors = {
        name: !f.name ? "This is required" : null,
        lastName: !f.lastName ? "This is required" : null,
        email: requiredError(
          f.email,
          !validateEmail(f.email) ? "Please enter a valid email address" : null
        ),
        state: !f.state ? "This is required" : null,
        city: !f.city ? "This is required" : null,
        zip: requiredError(
          f.zip,
          !validateZipCode(f.zip) ? "Please enter a valid ZIP code" : null
        ),
        phoneNumber: !validateUsPhoneNumber(f.phoneNumber)
          ? "Please enter a valid phone number"
          : null,
        latestEducation: requiredError(f?.jobSeeker?.latestEducation),
        isAdult: requiredError(f?.jobSeeker?.isAdult),
        authorizedInUS: requiredError(f?.jobSeeker?.authorizedInUS),
        requireSponsorship: requiredError(f?.jobSeeker?.requireSponsorship),
        validDriversLicense: requiredError(f?.jobSeeker?.validDriversLicense),
        reliableTransportation: requiredError(
          f?.jobSeeker?.reliableTransportation
        ),
        cdlClass:
          f?.jobSeeker?.validCdlLicense && isNullish(f?.jobSeeker?.cdlClass)
            ? "This is required"
            : null,
      }

      commit("SET_ERRORS", errors)
      const flatErrors = flattenErrors(errors)
      return Object.keys(flatErrors).length === 0
    },

    validateCandidateBusinessInfo({ commit, state }) {
      const f = state.fields
      const normalized = normalize(f)
      const jobSeeker = normalized?.jobSeeker || {}
      const {
        positionSeeking,
        hasManagementExperience,
        hasHandsOnExperience,
        handsOnExperiences,
      } = jobSeeker

      const handsOnExperiencesErrors = !handsOnExperiences
        ? null
        : handsOnExperiences.reduce((errors, experience) => {
            const { category, amount, rating } = experience
            const experienceError = {
              category: requiredError(category),
              amount: requiredError(amount),
              rating: requiredError(rating),
            }
            return [
              ...errors,
              Object.keys(normalize(deNullifyErrors(experienceError))).length >
              0
                ? experienceError
                : null,
            ]
          }, [])

      const errors = {
        positionSeeking: requiredError(positionSeeking),
        hasManagementExperience: requiredError(hasManagementExperience),
        hasHandsOnExperience: requiredError(hasHandsOnExperience),
        handsOnExperienceCategories: hasHandsOnExperience
          ? !handsOnExperiences
            ? "This is required"
            : null
          : null,
        handsOnExperiences:
          hasHandsOnExperience &&
          handsOnExperiencesErrors?.filter(Boolean).length > 0
            ? handsOnExperiencesErrors
            : null,
      }

      commit("SET_ERRORS", errors)
      const flatErrors = flattenErrors(errors)
      return Object.keys(flatErrors).length === 0
    },

    async validateCandidateAdditionalInfo({ commit, state }) {
      const f = state.fields
      const normalized = normalize(f)
      const jobSeeker = normalized?.jobSeeker || {}
      const { previousCompaniesWorkedFor, hasReferences, references } =
        jobSeeker

      const previousCompanyErrors = !previousCompaniesWorkedFor
        ? null
        : previousCompaniesWorkedFor.reduce((errors, company) => {
            const { position, from, to, current } = company
            const companyError = {
              position: requiredError(position),
              from: requiredError(from),
              to: !current ? requiredError(to) : null,
            }
            return [
              ...errors,
              Object.keys(normalize(deNullifyErrors(companyError))).length > 0
                ? companyError
                : null,
            ]
          }, [])

      const referencesErrors =
        !hasReferences || !references
          ? null
          : references.reduce((errors, reference) => {
              const { name, email, phone, relationship } = reference
              const error = {
                name: requiredError(name),
                email: requiredError(
                  email,
                  !validateEmail(email)
                    ? "Please enter a valid email address"
                    : null
                ),
                phone: requiredError(
                  phone,
                  !validateUsPhoneNumber(phone)
                    ? "Please enter a valid US phone number"
                    : null
                ),
                relationship: requiredError(relationship),
              }
              return [
                ...errors,
                Object.keys(normalize(deNullifyErrors(error))).length > 0
                  ? error
                  : null,
              ]
            }, [])

      const errors = {
        previousCompaniesWorkedFor:
          previousCompanyErrors?.filter(Boolean).length > 0
            ? previousCompanyErrors
            : null,
        references:
          referencesErrors?.filter(Boolean).length > 0
            ? referencesErrors
            : null,
        profilePhotoUpload: f.profilePhotoUpload
          ? await validatePhotos([f.profilePhotoUpload])
          : null,
        resumeUpload:
          f.resumeUpload && f.resumeUpload.size > 100 * 1024 * 1024
            ? `Files must be under ${100 / 1024 / 1024} MB in size`
            : null,
      }

      commit("SET_ERRORS", errors)
      const flatErrors = flattenErrors(errors)
      return Object.keys(flatErrors).length === 0
    },
  },
}
